import { useEffect, useMemo, useRef } from "react";
import { Feature } from "ol";
import { LineString, Point } from "ol/geom";
import { fromLonLat } from "ol/proj";
import { Fill, Stroke, Style } from "ol/style";
import CircleStyle from "ol/style/Circle";
import VectorLayer from "ol/layer/Vector";
import VectorSource from "ol/source/Vector";
import { buildMultiSegmentRoute, splitMultiSegmentRoute } from "./routeRenderer";
import { getMapThemeConfig } from "./mapThemes";

const createRouteStyles = (themeConfig) => {
  const routeColor = themeConfig?.routeColor || "#1814E6";
  const haloColor = themeConfig?.haloColor || "rgba(24, 20, 230, 0.35)";

  return {
    halo: new Style({
      stroke: new Stroke({
        width: 12,
        color: haloColor,
      }),
    }),
    completed: new Style({
      stroke: new Stroke({
        width: 5,
        color: routeColor,
      }),
    }),
    upcoming: new Style({
      stroke: new Stroke({
        width: 2.5,
        color: routeColor,
        lineDash: [8, 8],
      }),
    }),
    comet: new Style({
      image: new CircleStyle({
        radius: 8,
        fill: new Fill({ color: routeColor }),
        stroke: new Stroke({
          color: "#FFFFFF",
          width: 3,
        }),
      }),
    }),
  };
};

export const useFlightArc = ({
  map,
  counter = 0,
  waypoints,
  from,
  to,
  routeStyle = "flight",
  theme = "osm",
  edit = false,
}) => {
  const layerRef = useRef(null);
  const featuresRef = useRef(null);
  const routeCoordsRef = useRef([]);

  const effectiveWaypoints = useMemo(() => {
    return Array.isArray(waypoints) && waypoints.length >= 2
      ? waypoints
      : from && to
      ? [{ coordinate: from }, { coordinate: to }]
      : [];
  }, [waypoints, from, to]);

  // Generate route geometry when waypoints or routeStyle change
  useEffect(() => {
    if (effectiveWaypoints.length >= 2) {
      routeCoordsRef.current = buildMultiSegmentRoute(effectiveWaypoints, routeStyle);
    } else {
      routeCoordsRef.current = [];
    }
  }, [effectiveWaypoints, routeStyle]);

  // Setup vector layer on map
  useEffect(() => {
    if (!map || edit || routeCoordsRef.current.length < 2) {
      return;
    }

    const themeConfig = getMapThemeConfig(theme);
    const styles = createRouteStyles(themeConfig);

    const haloFeature = new Feature({
      type: "halo",
      geometry: new LineString([]),
    });
    haloFeature.setStyle(styles.halo);

    const completedFeature = new Feature({
      type: "completed",
      geometry: new LineString([]),
    });
    completedFeature.setStyle(styles.completed);

    const upcomingFeature = new Feature({
      type: "upcoming",
      geometry: new LineString([]),
    });
    upcomingFeature.setStyle(styles.upcoming);

    const cometFeature = new Feature({
      type: "comet",
      geometry: new Point([0, 0]),
    });
    cometFeature.setStyle(styles.comet);

    const source = new VectorSource({
      features: [haloFeature, completedFeature, upcomingFeature, cometFeature],
    });

    const vectorLayer = new VectorLayer({
      source,
      zIndex: 2,
    });

    map.addLayer(vectorLayer);
    layerRef.current = vectorLayer;
    featuresRef.current = {
      halo: haloFeature,
      completed: completedFeature,
      upcoming: upcomingFeature,
      comet: cometFeature,
    };

    return () => {
      map.removeLayer(vectorLayer);
      layerRef.current = null;
      featuresRef.current = null;
    };
  }, [map, edit, theme, effectiveWaypoints, routeStyle]);

  // Update progressive route when progress counter changes
  useEffect(() => {
    const features = featuresRef.current;
    if (!features || !Array.isArray(effectiveWaypoints) || effectiveWaypoints.length < 2) return;

    const { completedCoords, upcomingCoords, cometCoord, cometAngleRad } =
      splitMultiSegmentRoute(effectiveWaypoints, routeStyle, counter);

    const completedProjected = completedCoords.map((c) => fromLonLat(c));
    const upcomingProjected = upcomingCoords.map((c) => fromLonLat(c));
    const cometProjected = cometCoord ? fromLonLat(cometCoord) : [0, 0];

    if (completedProjected.length >= 2) {
      features.halo.getGeometry().setCoordinates(completedProjected);
      features.completed.getGeometry().setCoordinates(completedProjected);
    } else {
      features.halo.getGeometry().setCoordinates([]);
      features.completed.getGeometry().setCoordinates([]);
    }

    if (upcomingProjected.length >= 2) {
      features.upcoming.getGeometry().setCoordinates(upcomingProjected);
    } else {
      features.upcoming.getGeometry().setCoordinates([]);
    }

    if (cometCoord) {
      features.comet.getGeometry().setCoordinates(cometProjected);
      const cometStyle = features.comet.getStyle();
      if (cometStyle && typeof cometStyle.getImage === "function") {
        cometStyle.getImage().setRotation(-cometAngleRad);
      }
    }
  }, [map, counter, edit, theme, effectiveWaypoints, routeStyle]);
};
